<script>
  import { onMount } from 'svelte';
  import { fly, fade } from 'svelte/transition';
  import { db, initializeMeta, seedDemoData } from './lib/db.js';
  import { createUuid } from './lib/uuid.js';
  import { isSyncConfigured, pendingCount, startAutomaticSync, syncNow } from './lib/sync.js';

  let ready = false, fatalError = '', syncError = '', success = '';
  let step = 1, pending = 0, lastSync = '';
  let employees = [], zones = [], units = [], categories = [], variantTags = [], products = [];

  let employeeId = '';
  let zoneQuery = '', selectedZone = null;
  let descriptionSale = '', descriptionPurchase = '', reference = '', categoryName = '', unitName = '';
  let variantQuery = '', selectedVariants = [];
  let variantRows = [];
  let openList = '';

  $: selectedEmployee = employees.find((item) => item.id === Number(employeeId));
  $: productValid = descriptionSale.trim() && reference.trim() && categoryName.trim() && unitName.trim();
  $: editableRows = variantRows.filter((row) => !row.closed);
  $: rowsValid = editableRows.length > 0 && editableRows.every((row) =>
    Number.isFinite(Number(row.quantity)) && Number(row.quantity) !== 0 &&
    Math.abs(Number(row.quantity)) <= 1000000 &&
    Number(row.priceSale || 0) >= 0 && Number(row.pricePurchase || 0) >= 0);

  const norm = (value) => (value || '').trim().toLocaleLowerCase('fr');
  const contains = (haystack, needle) => norm(haystack).includes(norm(needle));

  $: zoneSuggestions = zoneQuery.trim()
    ? zones.filter((zone) => contains(zone.code, zoneQuery) || contains(zone.name, zoneQuery)).slice(0, 8)
    : zones.slice(0, 8);
  $: descSuggestions = descriptionSale.trim()
    ? products.filter((product) => contains(product.description_vente, descriptionSale)).slice(0, 8)
    : [];
  $: refSuggestions = reference.trim()
    ? products.filter((product) => contains(product.reference, reference)).slice(0, 8)
    : [];
  $: unitSuggestions = units.filter((unit) => !unitName.trim() || contains(unit.name, unitName)).slice(0, 8);
  $: catSuggestions = categories.filter((cat) => !categoryName.trim() || contains(cat.name, categoryName)).slice(0, 8);
  $: variantSuggestions = variantTags
    .filter((tag) => !selectedVariants.includes(tag.name) && (!variantQuery.trim() || contains(tag.name, variantQuery)))
    .slice(0, 8);
  $: variantCreatable = variantQuery.trim() &&
    !variantTags.some((tag) => norm(tag.name) === norm(variantQuery)) &&
    !selectedVariants.some((name) => norm(name) === norm(variantQuery));

  onMount(() => {
    let stopSync = () => {};
    (async () => {
      try {
        await initializeMeta(); await seedDemoData(); await refreshLocalState();
        const saved = await db.meta.get('current_employee_id');
        if (saved?.value && employees.some((item) => item.id === Number(saved.value))) {
          employeeId = String(saved.value);
          step = 2;
        }
        ready = true;
        stopSync = startAutomaticSync(async (error) => {
          syncError = error?.message || '';
          await refreshLocalState();
        });
      } catch (error) { fatalError = error instanceof Error ? error.message : 'Stockage local indisponible'; }
    })();
    return () => stopSync();
  });

  async function refreshLocalState() {
    [employees, zones, units, categories, variantTags, products, pending] = await Promise.all([
      db.employes.filter((item) => item.active !== false).sortBy('name'),
      db.zones.filter((item) => item.active !== false).sortBy('code'),
      db.unites.filter((item) => item.active !== false).sortBy('name'),
      db.categories.filter((item) => item.active !== false).sortBy('name'),
      db.variantes.filter((item) => item.active !== false).sortBy('name'),
      db.produits.filter((item) => item.active !== false).limit(500).toArray(),
      pendingCount(),
    ]);
    lastSync = (await db.meta.get('last_sync_at'))?.value || '';
  }

  async function confirmEmployee() {
    await db.meta.put({ key: 'current_employee_id', value: Number(employeeId) });
    success = '';
    step = 2;
  }

  function changeEmployee() { step = 1; openList = ''; }

  function changeZone() {
    // Les saisies déjà sauvegardées restent liées à leur zone d'origine —
    // on repart simplement sur un choix de zone vierge.
    zoneQuery = '';
    selectedZone = null;
    openList = '';
    step = 2;
  }

  function pickZone(zone) {
    selectedZone = zone;
    zoneQuery = zone.code;
    openList = '';
    step = 3;
  }

  function chooseZone() {
    const needle = norm(zoneQuery);
    const zone = zones.find((item) => norm(item.code) === needle || norm(item.name) === needle);
    if (zone) pickZone(zone);
    else selectedZone = null;
  }

  async function pickProduct(product) {
    reference = product.reference || '';
    descriptionSale = product.description_vente || '';
    descriptionPurchase = product.description_achat || '';
    const catId = Array.isArray(product.categorie_id) ? product.categorie_id[0] : product.categorie_id;
    const unitId = Array.isArray(product.unite_mesure_id) ? product.unite_mesure_id[0] : product.unite_mesure_id;
    categoryName = (await db.categories.get(catId))?.name || categoryName;
    unitName = (await db.unites.get(unitId))?.name || unitName;
    openList = '';
  }

  function pickUnit(unit) { unitName = unit.name; openList = ''; }
  function pickCategory(cat) { categoryName = cat.name; openList = ''; }

  function addVariant(name) {
    const cleaned = (name || '').trim();
    if (!cleaned || selectedVariants.some((item) => norm(item) === norm(cleaned))) return;
    selectedVariants = [...selectedVariants, cleaned];
    variantQuery = '';
  }

  function removeVariant(name) {
    selectedVariants = selectedVariants.filter((item) => item !== name);
  }

  function onVariantKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (variantSuggestions.length === 1) addVariant(variantSuggestions[0].name);
      else if (variantCreatable) addVariant(variantQuery);
    } else if (event.key === 'Backspace' && !variantQuery && selectedVariants.length) {
      removeVariant(selectedVariants[selectedVariants.length - 1]);
    }
  }

  function closeListSoon() { setTimeout(() => { openList = ''; }, 140); }

  async function existingLinesByVariant() {
    const map = new Map();
    const product = await db.produits.where('reference').equalsIgnoreCase(reference.trim()).first();
    if (!product || !selectedZone) return map;
    const productVariants = await db.produits_variantes
      .filter((item) => (Array.isArray(item.produit_id) ? item.produit_id[0] : item.produit_id) === product.id)
      .toArray();
    for (const variant of productVariants) {
      const tagName = Array.isArray(variant.variante_tag_id) ? variant.variante_tag_id[1] : '';
      const line = await db.stock_lignes
        .filter((item) =>
          (Array.isArray(item.produit_variante_id) ? item.produit_variante_id[0] : item.produit_variante_id) === variant.id &&
          (Array.isArray(item.zone_id) ? item.zone_id[0] : item.zone_id) === selectedZone.id)
        .first();
      if (line) map.set(norm(tagName), { quantity: line.quantite_totale, closed: line.statut_cloture === 'cloture' });
      map.set(`price:${norm(tagName)}`, { priceSale: variant.prix_vente, pricePurchase: variant.prix_achat });
    }
    return map;
  }

  async function prepareVariantRows() {
    const names = selectedVariants.length ? selectedVariants : [''];
    const existing = await existingLinesByVariant();
    variantRows = names.map((name) => {
      const line = existing.get(norm(name));
      const price = existing.get(`price:${norm(name)}`);
      return {
        name,
        priceSale: price?.priceSale ?? '',
        pricePurchase: price?.pricePurchase ?? '',
        quantity: '',
        existing: line?.quantity ?? null,
        closed: line?.closed ?? false,
      };
    });
    step = 4;
  }

  async function saveMovements() {
    if (!productValid || !rowsValid || !selectedEmployee || !selectedZone) return;
    const now = new Date().toISOString().replace('T', ' ').replace('Z', '').slice(0, 19);
    const rows = editableRows;
    await db.transaction('rw', db.mouvements, async () => {
      for (const row of rows) {
        await db.mouvements.add({
          uuid_client: createUuid(), statut: 'en_attente', created_at: Date.now(),
          zone_id: selectedZone.id, employe_id: selectedEmployee.id,
          quantite: Number(row.quantity), date_heure: now,
          produit: {
            reference: reference.trim(), description_vente: descriptionSale.trim(),
            description_achat: descriptionPurchase.trim(), categorie_nom: categoryName.trim(),
            unite_mesure_nom: unitName.trim(),
          },
          variante: {
            variante_tag_nom: row.name || null,
            prix_vente: Number(row.priceSale || 0), prix_achat: Number(row.pricePurchase || 0),
          },
        });
      }
    });
    success = `${rows.length} saisie${rows.length > 1 ? 's' : ''} sauvegardée${rows.length > 1 ? 's' : ''} ✓`;
    setTimeout(() => { success = ''; }, 4000);
    pending = await pendingCount();
    resetProduct(); step = 3;
    if (navigator.onLine && isSyncConfigured()) {
      try { await syncNow(); syncError = ''; await refreshLocalState(); } catch (error) { syncError = error.message; }
    }
  }

  function resetProduct() {
    descriptionSale = ''; descriptionPurchase = ''; reference = ''; categoryName = '';
    unitName = ''; variantQuery = ''; selectedVariants = []; variantRows = [];
  }
</script>

<main class="shell">
  <header class="topbar">
    <div class="brand"><img class="brand-logo" src="/logo.png" alt="SoonTech" /><span class="badge-terrain">Terrain</span></div>
    {#if selectedEmployee && step > 1}
      <button class="employee-chip" on:click={changeEmployee} title="Changer d'utilisateur">
        <span class="avatar">{selectedEmployee.name.slice(0, 1)}</span>
        <span class="employee-name">{selectedEmployee.name}</span>
        <span class="change-hint">changer</span>
      </button>
    {/if}
  </header>

  <div class="syncbar" class:warning={pending > 0}>
    <span class="sync-state"><span class="dot" class:online={navigator.onLine}></span>{navigator.onLine ? 'En ligne' : 'Hors ligne'} · {pending} en attente</span>
    <small>{lastSync ? `Sync : ${new Date(`${lastSync}Z`).toLocaleString('fr')}` : 'Jamais synchronisé'}</small>
  </div>

  {#if success}
    <div class="toast" transition:fly={{ y: -16, duration: 240 }}>{success}</div>
  {/if}

  {#if fatalError}
    <div class="status error"><strong>Impossible de démarrer</strong><span>{fatalError}</span></div>
  {:else if !ready}
    <div class="card loading"><span class="spinner"></span> Préparation…</div>
  {:else}
    <nav class="steps" aria-label="Progression">
      {#each ['Identité', 'Zone', 'Produit', 'Quantités'] as label, index}
        <span class="step-dot" class:active={step === index + 1} class:done={step > index + 1}>{label}</span>
      {/each}
    </nav>

    {#if syncError}<div class="status error" transition:fade>Données conservées localement. {syncError}</div>{/if}

    {#if step === 1}
      <section class="card" in:fly={{ y: 16, duration: 240 }}>
        <h1>Qui êtes-vous ?</h1>
        <label for="employee">Votre nom</label>
        <select id="employee" bind:value={employeeId}>
          <option value="">Choisir votre nom</option>
          {#each employees as employee}<option value={employee.id}>{employee.name}</option>{/each}
        </select>
        {#if !employees.length}<p class="field-error">Première synchronisation nécessaire pour charger les employés.</p>{/if}
        <p class="hint">Votre nom restera mémorisé sur cet appareil.</p>
        <button class="primary" disabled={!selectedEmployee} on:click={confirmEmployee}>Continuer</button>
      </section>

    {:else if step === 2}
      <section class="card" in:fly={{ y: 16, duration: 240 }}>
        <h1>Quel emplacement ?</h1>
        <label for="zone">Code ou nom de zone</label>
        <div class="suggest-wrap">
          <input id="zone" bind:value={zoneQuery} autocomplete="off" placeholder="Ex. A-12"
            on:focus={() => { openList = 'zone'; }} on:input={() => { openList = 'zone'; selectedZone = null; }}
            on:blur={closeListSoon}
            on:keydown={(event) => event.key === 'Enter' && chooseZone()} />
          {#if openList === 'zone' && zoneSuggestions.length}
            <ul class="suggest" transition:fade={{ duration: 120 }}>
              {#each zoneSuggestions as zone}
                <li><button type="button" on:mousedown|preventDefault={() => pickZone(zone)}>
                  <strong>{zone.code}</strong><span>{zone.name}</span>
                </button></li>
              {/each}
            </ul>
          {/if}
        </div>
        {#if zoneQuery && !selectedZone && !zoneSuggestions.length}
          <p class="field-error">Cet emplacement n'existe pas</p>
        {/if}
        <button class="primary" disabled={!zoneQuery.trim()} on:click={chooseZone}>Valider l'emplacement</button>
      </section>

    {:else if step === 3}
      <section class="card" in:fly={{ y: 16, duration: 240 }}>
        <button class="back" on:click={changeZone}>← Passer à une autre zone</button>
        <h1>Nouveau produit</h1>
        <button type="button" class="location" on:click={changeZone} title="Changer d'emplacement">
          📍 {selectedZone.code} — {selectedZone.name}
          <span class="loc-change">changer</span>
        </button>

        <label for="sale-desc">Description de vente *</label>
        <div class="suggest-wrap">
          <textarea id="sale-desc" rows="2" bind:value={descriptionSale}
            on:focus={() => { openList = 'desc'; }} on:input={() => { openList = 'desc'; }} on:blur={closeListSoon}></textarea>
          {#if openList === 'desc' && descSuggestions.length}
            <ul class="suggest" transition:fade={{ duration: 120 }}>
              {#each descSuggestions as product}
                <li><button type="button" on:mousedown|preventDefault={() => pickProduct(product)}>
                  <strong>{product.reference}</strong><span>{product.description_vente}</span>
                </button></li>
              {/each}
            </ul>
          {/if}
        </div>

        <label for="purchase-desc">Description d'achat</label>
        <textarea id="purchase-desc" rows="2" bind:value={descriptionPurchase}></textarea>

        <div class="two-cols">
          <div>
            <label for="reference">Référence *</label>
            <div class="suggest-wrap">
              <input id="reference" bind:value={reference} autocomplete="off"
                on:focus={() => { openList = 'ref'; }} on:input={() => { openList = 'ref'; }} on:blur={closeListSoon} />
              {#if openList === 'ref' && refSuggestions.length}
                <ul class="suggest" transition:fade={{ duration: 120 }}>
                  {#each refSuggestions as product}
                    <li><button type="button" on:mousedown|preventDefault={() => pickProduct(product)}>
                      <strong>{product.reference}</strong><span>{product.description_vente}</span>
                    </button></li>
                  {/each}
                </ul>
              {/if}
            </div>
          </div>
          <div>
            <label for="unit">Unité *</label>
            <div class="suggest-wrap">
              <input id="unit" bind:value={unitName} autocomplete="off" placeholder="Pièce, kg…"
                on:focus={() => { openList = 'unit'; }} on:input={() => { openList = 'unit'; }} on:blur={closeListSoon} />
              {#if openList === 'unit' && unitSuggestions.length}
                <ul class="suggest" transition:fade={{ duration: 120 }}>
                  {#each unitSuggestions as unit}
                    <li><button type="button" on:mousedown|preventDefault={() => pickUnit(unit)}><strong>{unit.name}</strong></button></li>
                  {/each}
                </ul>
              {/if}
            </div>
          </div>
        </div>

        <label for="category">Catégorie *</label>
        <div class="suggest-wrap">
          <input id="category" bind:value={categoryName} autocomplete="off"
            on:focus={() => { openList = 'cat'; }} on:input={() => { openList = 'cat'; }} on:blur={closeListSoon} />
          {#if openList === 'cat' && catSuggestions.length}
            <ul class="suggest" transition:fade={{ duration: 120 }}>
              {#each catSuggestions as cat}
                <li><button type="button" on:mousedown|preventDefault={() => pickCategory(cat)}><strong>{cat.name}</strong></button></li>
              {/each}
            </ul>
          {/if}
        </div>

        <label for="variants">Variantes</label>
        <div class="tags-field suggest-wrap" class:focused={openList === 'variant'}>
          {#each selectedVariants as name (name)}
            <span class="tag" transition:fade={{ duration: 120 }}>
              {name}
              <button type="button" class="tag-remove" on:click={() => removeVariant(name)} aria-label={`Retirer ${name}`}>×</button>
            </span>
          {/each}
          <input id="variants" class="tag-input" bind:value={variantQuery} autocomplete="off"
            placeholder={selectedVariants.length ? '' : 'Rouge, XL, 500g…'}
            on:focus={() => { openList = 'variant'; }} on:input={() => { openList = 'variant'; }}
            on:blur={closeListSoon} on:keydown={onVariantKeydown} />
          {#if openList === 'variant' && (variantSuggestions.length || variantCreatable)}
            <ul class="suggest" transition:fade={{ duration: 120 }}>
              {#each variantSuggestions as tag}
                <li><button type="button" on:mousedown|preventDefault={() => addVariant(tag.name)}><strong>{tag.name}</strong></button></li>
              {/each}
              {#if variantCreatable}
                <li><button type="button" class="create" on:mousedown|preventDefault={() => addVariant(variantQuery)}>+ Créer «{variantQuery.trim()}»</button></li>
              {/if}
            </ul>
          {/if}
        </div>
        <p class="hint">Touchez une suggestion pour l'ajouter, ou tapez puis Entrée pour créer.</p>

        <button class="primary" disabled={!productValid} on:click={prepareVariantRows}>Prix et quantité →</button>
      </section>

    {:else}
      <section class="card" in:fly={{ y: 16, duration: 240 }}>
        <button class="back" on:click={() => { step = 3; }}>← Produit</button>
        <h1>Prix et quantité</h1>
        <p class="location">📍 {selectedZone.code} · {reference}</p>

        <div class="grid-table" role="table" aria-label="Prix et quantités par variante">
          <div class="grid-head" role="row">
            <span>Variante</span><span>Prix achat</span><span>Prix vente</span><span>Quantité *</span>
          </div>
          {#each variantRows as row, index (row.name)}
            <div class="grid-row" class:closed={row.closed} role="row" in:fade={{ duration: 160, delay: index * 40 }}>
              <span class="variant-name">
                {row.name || 'Sans variante'}
                {#if row.closed}<span class="badge closed-badge">Clôturé</span>
                {:else if row.existing !== null}<span class="badge">déjà : {row.existing}</span>{/if}
              </span>
              <span data-label="Prix achat"><input id={`purchase-${index}`} type="number" inputmode="decimal" min="0" step="0.01" bind:value={row.pricePurchase} disabled={row.closed} /></span>
              <span data-label="Prix vente"><input id={`sale-${index}`} type="number" inputmode="decimal" min="0" step="0.01" bind:value={row.priceSale} disabled={row.closed} /></span>
              <span data-label="Quantité *"><input id={`quantity-${index}`} type="number" inputmode="decimal" step="any" bind:value={row.quantity} disabled={row.closed} placeholder="0" /></span>
            </div>
          {/each}
        </div>
        {#if variantRows.some((row) => row.quantity !== '' && (!Number(row.quantity) || Math.abs(Number(row.quantity)) > 1000000))}
          <p class="field-error">Chaque quantité doit être non nulle (max 1 000 000).</p>
        {/if}
        {#if !editableRows.length}
          <p class="field-error">Toutes les lignes sont clôturées — modification impossible.</p>
        {/if}

        <button class="primary" disabled={!rowsValid} on:click={saveMovements}>Sauvegarder</button>
      </section>
    {/if}
  {/if}
</main>
